import AIButton from '@cobliteam/ai/src/ui/AIButton/AIButton';
import type { RiskRecognitionHighlight } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/Types';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { formatDateTZ } from '@cobliteam/utils/date';
import { formatRiskRecognitionCardDisplay, getRiskRecognitionIcon } from '../../types/risk-recognition/format';
import RiskEvidence from '../../types/risk-recognition/RiskEvidence';
import RiskHighlightText, { renderRiskHighlight } from '../../types/risk-recognition/RiskHighlightText';

interface RiskNotificationRowProps {
  highlight: RiskRecognitionHighlight;
  isArchivedView: boolean;
  onOpen: (highlight: RiskRecognitionHighlight) => void;
  onArchiveToggle: (highlight: RiskRecognitionHighlight) => void;
}

/**
 * Linha de notificação de risco compartilhada pelo sino (1ª página) e pela página dedicada (scroll infinito).
 * Compacta: ícone do tipo, título/descrição (com `<bold>`), motorista · veículo, badge de criticidade, tempo
 * relativo e chip de footage quando há `signed_media_urls`. Clicar abre o drawer (detalhe completo + player);
 * o botão arquivar/desarquivar é inline.
 */
const RiskNotificationRow = ({ highlight, isArchivedView, onOpen, onArchiveToggle }: RiskNotificationRowProps) => {
  const { t } = useTranslation('notifications');
  const { headline, description } = formatRiskRecognitionCardDisplay(highlight);
  const iconName = getRiskRecognitionIcon(highlight);

  return (
    <div className='flex gap-3 px-6 py-4 transition-colors focus-within:bg-gray-2'>
      <span className={cn('mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full', 'bg-danger-3 text-danger-9')} aria-hidden>
        <Icon name={iconName} size='sm' />
      </span>

      <div className='min-w-0 flex-1 space-y-2'>
        <Typography variant='description-title' className='wrap-anywhere text-balance text-danger-10 leading-135'>
          {renderRiskHighlight(headline, 'font-semibold')}
        </Typography>

        <div className='flex items-start gap-3'>
          <RiskEvidence highlight={highlight} surface='bell' size='md' onOpen={onOpen} />

          {description ? <RiskHighlightText text={description} variant='text-sm' className='wrap-anywhere text-pretty text-gray-11 leading-5' /> : null}
        </div>

        <div className='flex items-center justify-between gap-2'>
          <Typography as='span' variant='caption-xs' color='secondary' className='tabular-nums'>
            {formatDateTZ(highlight.created_at, 'dd MMM, HH:mm').replace('.', '')}
          </Typography>
          <div className='flex items-center gap-2'>
            <Button
              type='button'
              size='sm'
              variant='outlined'
              color='default'
              aria-label={isArchivedView ? t('feed.actions.unarchive') : t('feed.actions.archive')}
              title={isArchivedView ? t('feed.actions.unarchive') : t('feed.actions.archive')}
              onClick={() => onArchiveToggle(highlight)}
            >
              {isArchivedView ? t('feed.actions.unarchive') : t('feed.actions.archive')}
            </Button>
            <AIButton
              variant='ai-outline'
              icon={false}
              shape='cta'
              size='sm'
              showTooltip={false}
              label={t('risk_recognition.action')}
              className='active:scale-[0.96]'
              onClick={() => onOpen(highlight)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskNotificationRow;
